import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Camera, X, ClipboardText } from '@phosphor-icons/react';

/**
 * Modal scanner. Calls onResult(text) when a QR is decoded successfully.
 * Falls back to a paste-text input if camera scanning is unavailable / denied.
 */
export default function QrScanner({ onResult, onClose }) {
    const containerId = 'qr-reader-region';
    const scannerRef = useRef(null);
    const onResultRef = useRef(onResult);
    const [error, setError] = useState(null);
    const [pasteValue, setPasteValue] = useState('');

    // Keep the latest onResult in a ref so the scanner effect doesn't have
    // to re-run (and re-init the camera) every time the parent re-renders.
    useEffect(() => { onResultRef.current = onResult; }, [onResult]);

    useEffect(() => {
        let mounted = true;
        let instance = null;
        let started = false;  // only true once instance.start() resolved

        (async () => {
            try {
                instance = new Html5Qrcode(containerId, { verbose: false });
                scannerRef.current = instance;
                const cams = await Html5Qrcode.getCameras();
                if (!mounted) return;
                if (!cams || cams.length === 0) {
                    setError('No camera found on this device. Paste the reply code instead.');
                    return;
                }
                // Prefer back/environment-facing if available.
                const back = cams.find(c => /back|rear|environment/i.test(c.label)) || cams[cams.length - 1];
                await instance.start(
                    back.id,
                    { fps: 10, qrbox: { width: 240, height: 240 } },
                    (decoded) => {
                        if (!decoded) return;
                        // Guard: stop() throws synchronously when scanner
                        // isn't running. try/catch around the call itself.
                        try { instance.stop().catch(() => {}); } catch { /* noop */ }
                        onResultRef.current?.(decoded);
                    },
                    () => { /* per-frame errors are noisy; ignore */ }
                );
                started = true;
            } catch (e) {
                setError('Camera unavailable. ' + (e?.message || '') + ' You can paste the reply code below.');
            }
        })();

        return () => {
            mounted = false;
            const inst = scannerRef.current;
            if (!inst) return;
            // Only attempt to stop if the scanner actually started. stop()
            // throws SYNCHRONOUSLY ('Cannot stop, scanner is not running or
            // paused') when called on a never-started or already-stopped
            // scanner, bypassing .catch(). getState() handles both cases.
            let state;
            try { state = inst.getState?.(); } catch { state = null; }
            const SCANNING = 2, PAUSED = 3;
            if (started && (state === SCANNING || state === PAUSED)) {
                try {
                    inst.stop().catch(() => {}).finally(() => {
                        try { inst.clear(); } catch { /* noop */ }
                    });
                } catch {
                    try { inst.clear(); } catch { /* noop */ }
                }
            } else {
                try { inst.clear(); } catch { /* noop */ }
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submitPaste = (e) => {
        e.preventDefault();
        if (pasteValue.trim()) onResult(pasteValue.trim());
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 no-print" data-testid="qr-scanner-modal">
            <div className="bg-white w-full max-w-md border border-[#18181B]">
                <div className="flex items-center justify-between px-4 py-3 border-b border-[#18181B]">
                    <div className="flex items-center gap-2">
                        <Camera size={18} weight="bold" />
                        <span className="font-display font-semibold">Scan builder reply</span>
                    </div>
                    <button onClick={onClose} data-testid="close-scanner-btn" className="p-1 hover:bg-[#F4F4F5]"><X size={18} /></button>
                </div>
                <div className="p-4">
                    <div id={containerId} className="w-full bg-[#09090B] aspect-square overflow-hidden" />
                    {error && (
                        <div className="mt-3 border border-[#E4E4E7] p-3 text-xs text-[#52525B]">{error}</div>
                    )}
                    <form onSubmit={submitPaste} className="mt-4">
                        <div className="label-tiny mb-2 inline-flex items-center gap-1"><ClipboardText size={12} /> Or paste reply code</div>
                        <textarea
                            data-testid="paste-reply-input"
                            value={pasteValue}
                            onChange={(e) => setPasteValue(e.target.value)}
                            rows={3}
                            className="w-full border border-[#E4E4E7] focus:border-[#18181B] outline-none px-3 py-2 font-mono text-xs"
                            placeholder="Paste the reply code here…"
                        />
                        <button
                            type="submit"
                            data-testid="apply-paste-btn"
                            className="mt-2 w-full bg-[#1FA5CF] text-white py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#1787AB] active:scale-95 transition-all duration-150"
                        >Apply pasted reply</button>
                    </form>
                </div>
            </div>
        </div>
    );
}
